import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { NavigationButton } from './NavigationButton';
import { NavigationSection } from '../../config/navigationConfig';

interface SidebarSectionProps {
  section: NavigationSection;
  currentPage: string;
  onPageChange: (page: string) => void;
  isCollapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

export function SidebarSection({
  section,
  currentPage,
  onPageChange,
  isCollapsed,
  isOpen,
  onToggle,
}: SidebarSectionProps) {
  // Check if this section contains the active page
  const hasActivePage = section.items.some(item => item.page === currentPage);

  // When collapsed, just show items as flat list
  if (isCollapsed) {
    return (
      <>
        {section.items.map((item) => (
          <NavigationButton
            key={item.id}
            icon={item.icon}
            label={item.label}
            isActive={currentPage === item.page}
            onClick={() => onPageChange(item.page)}
            collapsed={true}
          />
        ))}
      </>
    );
  }

  return (
    <div className="space-y-1">
      {/* Section Header */}
      <motion.div
        className={`
          relative overflow-hidden backdrop-blur-xl cursor-pointer
          box-border flex flex-row gap-3 items-center px-3 py-2 rounded-xl
          transition-all duration-200
          border border-transparent
          ${hasActivePage 
            ? 'bg-white/8 dark:bg-gray-800/25 border-white/10 dark:border-gray-700/25' 
            : 'hover:bg-white/12 dark:hover:bg-gray-800/35 hover:border-white/15 dark:hover:border-gray-700/30'
          }
        `}
        onClick={onToggle}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      >
        <section.icon
          className={`
            relative shrink-0 transition-colors duration-200
            ${hasActivePage 
              ? 'text-gray-800 dark:text-gray-200' 
              : 'text-gray-700 dark:text-gray-300'
            }
          `}
          size={20}
        />
        <span className={`
          font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] 
          font-[590] text-[14px] flex-1 transition-colors duration-200
          ${hasActivePage 
            ? 'text-gray-800 dark:text-gray-200' 
            : 'text-gray-700 dark:text-gray-300'
          }
        `}>
          {section.label}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          <ChevronDown size={18} className={`
            transition-colors duration-200
            ${hasActivePage 
              ? 'text-gray-600 dark:text-gray-300' 
              : 'text-gray-500 dark:text-gray-400'
            }
          `} />
        </motion.div>
      </motion.div>

      {/* Section Items */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden space-y-1"
          >
            {section.items.map((item) => (
              <NavigationButton
                key={item.id}
                icon={item.icon}
                label={item.label}
                isActive={currentPage === item.page}
                onClick={() => onPageChange(item.page)}
                collapsed={false}
                indent={true}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

